/**
 * Console micro-charts — inline SVG, zero dependencies, feature-local.
 *
 * Dataviz rules applied (single-series, so no categorical palette to
 * validate): history bars wear the de-emphasis step of ONE hue and the
 * current period wears the accent; text stays in text tokens (never the
 * series color); zero days render as zero (the server zero-fills — a flat
 * chart is honest signal); every bar carries a native <title> hover tooltip.
 */
import { useId } from "react";

export interface SeriesPoint {
  day: string; // YYYY-MM-DD
  count: number;
}

/** Compact value: 1284 → 1.3K, 4200000 → 4.2M. */
export function compact(value: number): string {
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (Math.abs(value) >= 10_000) return `${(value / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  return value.toLocaleString("en-US");
}

/**
 * Bar sparkline for a zero-filled daily series (counts per UTC day). Bars,
 * not a line — these are discrete daily magnitudes, and a 14-integer line
 * would invent slopes between days.
 */
export function SparkBars({
  series,
  width = 132,
  height = 36,
  title,
}: {
  series: SeriesPoint[];
  width?: number;
  height?: number;
  title: string;
}) {
  const id = useId();
  const max = Math.max(1, ...series.map((p) => p.count));
  const gap = 2;
  const barW = (width - gap * (series.length - 1)) / Math.max(1, series.length);
  return (
    <svg
      className="admin__spark"
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-labelledby={id}
    >
      <title id={id}>{title}</title>
      {/* recessive baseline so all-zero days still read as "measured, zero" */}
      <line x1={0} y1={height - 0.5} x2={width} y2={height - 0.5} className="admin__spark-base" />
      {series.map((p, i) => {
        const h = Math.round(((height - 3) * p.count) / max);
        const isCurrent = i === series.length - 1;
        return (
          <rect
            key={p.day}
            className={isCurrent ? "admin__spark-bar admin__spark-bar--now" : "admin__spark-bar"}
            x={i * (barW + gap)}
            y={height - Math.max(h, p.count > 0 ? 2 : 0)}
            width={barW}
            height={Math.max(h, p.count > 0 ? 2 : 0)}
            rx={1.5}
          >
            <title>{`${p.day} — ${p.count}`}</title>
          </rect>
        );
      })}
    </svg>
  );
}

/**
 * Labeled horizontal meter row (breakdown panels). Identity lives in the row
 * label, magnitude in the fill length — one hue, no legend needed.
 */
export function MeterRow({ label, value, max }: { label: string; value: number; max: number }) {
  const pct = max > 0 ? Math.max(value > 0 ? 2 : 0, Math.round((value / max) * 100)) : 0;
  return (
    <div className="admin__meter" role="img" aria-label={`${label}: ${value}`}>
      <span className="admin__meter-label">{label}</span>
      <span className="admin__meter-track">
        <span className="admin__meter-fill" style={{ width: `${pct}%` }} />
      </span>
      <span className="admin__meter-value">{compact(value)}</span>
    </div>
  );
}
